import React, { useCallback } from 'react';
import { Chip, useTheme } from 'react-native-paper';
import { DrawerNavigationProp } from '@react-navigation/drawer';
import { CompositeScreenProps, useNavigation } from '@react-navigation/native';
import { Linking, View } from 'react-native';
import { StackScreenProps } from '@react-navigation/stack';
import { t } from 'i18n-js';
import { useI18n } from '../../context/I18nContext';
import { screenNames } from '../../navigators/screenNames';
import ScrollablePage from '../../components/ui/ScrollablePage';
import { AppDrawerScreenProp } from '../../components/DrawerMenuContent';
import { AuthState, useAuth } from '../../context/AuthContext';
import { ListItemRow, ListItemRowProps } from '../../components/ListItemRow';
import { Currency, PoisType, Product, ProductPaymentType, ProductSubscriptionStatus } from '../../types/appsync-types';
import Typography from '../../components/Typography';
import { UiIcon } from '../../components/SimpleListIcon';
import { useGetMyUnpaidReservationsForProductsList, useGetProductsByPodId } from '../../apis/subscriptionApis';
import Loading from '../../components/Loading';
import { useMapViewFilter } from '../../context/MapViewFilterContext';
import { useRefetchOnFocus } from '../../hooks/useRefetchOnFocus';
import { MapStackRouteParams } from '../../navigators/MapStackNavigator';
import { useGetSpaceTerms } from '../../apis/mapApis';
import { useParkingTermsUrlForCurrentLocale } from '../../hooks/useParkingTermsUrlForCurrentLocale';

function ProductItem({
  name,
  productId,
  isReservation,
  isFree,
  price,
  owner,
  currency,
  active,
  status,
  endsAt,
  startDate,
}: {
  name: string;
  productId: string;
  isReservation: boolean;
  isFree: boolean;
  price?: number | undefined | null; // TODO: What to do if price is undefined or null?
  owner: string | undefined | null;
  currency: Currency;
  active: boolean;
  status: ProductSubscriptionStatus | undefined;
  endsAt: number | undefined | null;
  startDate: string | undefined | null;
}) {
  const { formatCurrency, formatDate } = useI18n();
  const { colors } = useTheme();
  const { I18n } = useI18n();
  const { timeZone } = useMapViewFilter();
  const navigation = useNavigation<DrawerNavigationProp<any>>();

  const onPress = useCallback(() => {
    if (isReservation) {
      navigation.navigate(screenNames.ReservationProductDetailScreen, {
        productId,
      });
    } else {
      navigation.navigate(screenNames.OneTimePurchaseProductDetailScreen, {
        productId,
      });
    }
  }, [isReservation, navigation, productId]);

  let subscriptionStatusText: string | null = null;
  let icon: ListItemRowProps['leftIcon'] = 'clock';
  if (active) {
    switch (status) {
      case ProductSubscriptionStatus.CANCELLING:
        subscriptionStatusText = I18n.t('oneTimePurchase.cancelling');
        icon = { name: 'check', set: 'material' };
        break;
      case ProductSubscriptionStatus.REQUIRES_ACTION:
      case ProductSubscriptionStatus.ENDED_NOT_PAID:
        subscriptionStatusText = I18n.t('oneTimePurchase.actionRequired');
        icon = { name: 'alert-circle', set: 'material-community' };
        break;
      case ProductSubscriptionStatus.PROCESSING:
        subscriptionStatusText = I18n.t('oneTimePurchase.processing');
        icon = { name: 'progress-clock', set: 'material-community' };
        break;
      case ProductSubscriptionStatus.FAILED:
        subscriptionStatusText = I18n.t('oneTimePurchase.failed');
        icon = { name: 'close-circle', set: 'material-community' };
        break;
      case ProductSubscriptionStatus.RESERVED:
        subscriptionStatusText = I18n.t('oneTimePurchase.activeSince', {
          date: startDate ? formatDate(new Date(startDate), 'timeAndDate', timeZone) : '',
        });
        icon = { name: 'check', set: 'material' };
        break;
      default:
        subscriptionStatusText = I18n.t('oneTimePurchase.activeUntil', {
          date: endsAt ? formatDate(new Date(endsAt), 'timeAndDate', timeZone) : '',
        });
        icon = { name: 'check', set: 'material' };
        break;
    }
  }

  const renderTitle = () => {
    return (
      <Typography variant="body1">
        {name}
        {subscriptionStatusText && (
          <Typography variant="body1">
            {' • '}
            <Typography variant="body1" color={colors.primary} style={{ fontWeight: 'bold' }}>
              {subscriptionStatusText}
            </Typography>
          </Typography>
        )}
      </Typography>
    );
  };

  const descriptionOwner = owner ? `${owner}${isReservation || isFree ? '' : ' •'}` : '';

  return (
    <ListItemRow
      title={renderTitle}
      description={`${descriptionOwner} ${!isReservation && !isFree ? formatCurrency(price, currency) : ''}`}
      leftIcon={icon}
      onPress={onPress}
      accessibilityRole="button"
      type="navigation"
    />
  );
}

export type OneTimePurchaseProductsRouteParams = {
  filter?: string;
  podId?: string;
  podType?: PoisType;
};

type Props = CompositeScreenProps<
  StackScreenProps<MapStackRouteParams, 'OneTimePurchaseProductsScreen'>,
  AppDrawerScreenProp
>;

export const OneTimePurchaseProductsScreen: React.FC<Props> = ({ route }) => {
  const { I18n } = useI18n();
  const { colors } = useTheme();
  const { state } = useAuth();
  const navigation = useNavigation<DrawerNavigationProp<any>>();
  const { filter: filterParam, podId, podType } = route.params || {};
  const [filter, setFilter] = React.useState<string | null>(filterParam || null);

  const getProductsResult = useGetProductsByPodId(podId);
  useRefetchOnFocus(getProductsResult.refetch);
  const getUnpaidProductReservations = useGetMyUnpaidReservationsForProductsList();

  const getSpaceTermsResult = useGetSpaceTerms(podId);
  const parkingTermsUrl = useParkingTermsUrlForCurrentLocale(
    getSpaceTermsResult.data?.data?.getPoisByID?.metadata?.parkingTerms,
  );

  // TODO: Add some sort of distinction between adhoc and prepaid products to UI
  const products =
    (getProductsResult.data?.data?.getProducts ?? getProductsResult.data?.data?.getProductsByPodId)
      ?.filter<Product>(
        (product): product is Product => !!product && product.paymentType !== ProductPaymentType.SUBSCRIPTION,
      )
      .sort((a) =>
        a.paymentType === ProductPaymentType.RESERVATION || a.paymentType === ProductPaymentType.FREE ? -1 : 1,
      ) ?? [];

  const unpaidReservations =
    getUnpaidProductReservations.data?.data?.getMyUnpaidReservations?.filter((reservation) => !!reservation) ?? [];

  const isActiveProductsView = filterParam === 'active';

  const filteredProducts = filter
    ? products.filter((sub) => (filter === 'active' ? !!sub.subscription : !sub.subscription))
    : products;

  const renderNoProductsContent = () => {
    return getProductsResult.isLoading || getUnpaidProductReservations.isLoading ? (
      <Loading />
    ) : (
      <Typography variant="body1" style={{ paddingHorizontal: 16, paddingVertical: 8 }}>
        {filter === 'active' ? t('oneTimePurchase.noActiveProducts') : t('oneTimePurchase.noProducts')}
      </Typography>
    );
  };

  return (
    <ScrollablePage
      summary={{
        title:
          podType === PoisType.LOCKER
            ? I18n.t('lockerInfo.purchase.summaryTitle')
            : I18n.t('oneTimePurchase.summaryTitle'),
        description:
          podType === PoisType.LOCKER
            ? isActiveProductsView
              ? I18n.t('lockerInfo.purchase.activeSummaryDescription')
              : I18n.t('lockerInfo.purchase.summaryDescription')
            : isActiveProductsView
            ? I18n.t('oneTimePurchase.activeSummaryDescription')
            : I18n.t('oneTimePurchase.summaryDescription'),
        icon: isActiveProductsView ? 'clock' : 'basket',
        button: parkingTermsUrl
          ? {
              label: I18n.t('oneTimePurchase.terms'),
              onPress: () => {
                Linking.openURL(parkingTermsUrl);
              },
            }
          : undefined,
      }}
    >
      {/* {state !== AuthState.GUEST_LOGIN && !filterParam && (
        <View style={{ flexDirection: 'row', paddingLeft: 16, paddingBottom: 8, alignItems: 'center' }}>
          <UiIcon size={24} name="sliders" set="fontawesome6" color={colors.onSurface} />
          {['active', 'inactive'].map((item) => {
            const selected = filter === item;
            return (
              <Chip
                key={item}
                style={{ marginLeft: 8, backgroundColor: selected ? colors.primary : undefined }}
                mode={selected ? 'flat' : 'outlined'}
                selectedColor={selected ? colors.inverseOnSurface : undefined}
                selected={selected}
                onPress={(e) => (selected ? setFilter(null) : setFilter(item))}
              >
                {I18n.t(`oneTimePurchase.filter.${item}`)}
              </Chip>
            );
          })}
        </View>
      )} */}
      {filteredProducts.length || ((!filter || filter === 'active') && unpaidReservations.length) ? (
        <>
          {unpaidReservations?.map((reservation) => (
            <ProductItem
              key={reservation.id}
              name={reservation.product?.name ?? ''}
              productId={reservation.product?.id ?? ''}
              owner={reservation.product?.owner?.name}
              currency={(reservation?.currency as Currency) || Currency.EUR}
              active={true}
              status={reservation.status}
              endsAt={reservation.endsAt}
              startDate={reservation?.startDate}
              isReservation={true}
              isFree={false}
            />
          ))}
          {filteredProducts?.map((product) => (
            <ProductItem
              key={product.id}
              name={product.name ?? ''}
              productId={product.id ?? ''}
              price={product.subscription?.price ?? product.price}
              owner={product.owner?.name}
              currency={(product.subscription?.currency as Currency) || Currency.EUR}
              active={!!product.subscription}
              status={product.subscription?.status}
              endsAt={product.subscription?.endsAt}
              startDate={product.subscription?.startDate}
              isReservation={product.paymentType === ProductPaymentType.RESERVATION}
              isFree={product.paymentType === ProductPaymentType.FREE}
            />
          ))}
        </>
      ) : (
        renderNoProductsContent()
      )}
    </ScrollablePage>
  );
};
